// Firebase JS SDK init for the admin app.
//
// Config comes from Vite envs (see .env.example). Both the API key and the
// rest of the web-SDK config are safe to ship in the bundle — Auth gating
// happens server-side (admin-users allow-list + @commonequity.com domain
// check). Documented in admin/ARCHITECTURE.md §4.

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  type Auth,
  type User,
} from 'firebase/auth';

const requiredEnv = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

function getEnv(key: string): string | undefined {
  // Vite replaces import.meta.env.<KEY> at build time.
  return (import.meta.env as Record<string, string | undefined>)[key];
}

const missing = requiredEnv.filter((k) => !getEnv(k));
if (missing.length > 0 && import.meta.env.DEV) {
  // Warn loudly in dev so the operator notices — don't throw, so the shell
  // still renders and the operator can read the warning in the console.
  // eslint-disable-next-line no-console
  console.warn(
    '[firebase] Missing Vite envs:',
    missing.join(', '),
    '\nCopy .env.example to .env.local and fill them in.',
  );
}

export const firebaseApp: FirebaseApp = initializeApp({
  apiKey: getEnv('VITE_FIREBASE_API_KEY') ?? '',
  authDomain: getEnv('VITE_FIREBASE_AUTH_DOMAIN') ?? '',
  projectId: getEnv('VITE_FIREBASE_PROJECT_ID') ?? '',
  storageBucket: getEnv('VITE_FIREBASE_STORAGE_BUCKET') ?? '',
  messagingSenderId: getEnv('VITE_FIREBASE_MESSAGING_SENDER_ID') ?? '',
  appId: getEnv('VITE_FIREBASE_APP_ID') ?? '',
});

export const auth: Auth = getAuth(firebaseApp);

export const WORKSPACE_DOMAIN =
  getEnv('VITE_GOOGLE_WORKSPACE_DOMAIN') ?? 'commonequity.com';

export function googleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({
    hd: WORKSPACE_DOMAIN,
    prompt: 'select_account',
  });
  return provider;
}

export async function signInWithGoogle(): Promise<User> {
  const result = await signInWithPopup(auth, googleProvider());
  return result.user;
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

export { onAuthStateChanged };
export type { User };

/**
 * Cached-ID-token getter. Firebase already caches tokens for ~1h; we only
 * force-refresh if the cached one is older than 50 minutes, so we don't burn
 * a refresh on every request.
 */
let cachedToken: { token: string; expiresAt: number } | null = null;
const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * 1000; // 10 min before the 60-min expiry

export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    cachedToken = null;
    return null;
  }
  const now = Date.now();
  if (
    !forceRefresh &&
    cachedToken &&
    cachedToken.expiresAt - now > REFRESH_BEFORE_EXPIRY_MS
  ) {
    return cachedToken.token;
  }
  const token = await user.getIdToken(forceRefresh);
  // Firebase tokens last 60 minutes; we approximate the expiry locally.
  cachedToken = { token, expiresAt: now + 60 * 60 * 1000 };
  return token;
}

export function clearTokenCache(): void {
  cachedToken = null;
}

export function isWorkspaceEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const re = new RegExp(
    `^[^@\\s]+@${WORKSPACE_DOMAIN.replace(/\./g, '\\.')}$`,
    'i',
  );
  return re.test(email);
}
